/**
 * +ranger-specs: the Ranger specs' HUD pieces (docs/design/classes/specs.md "Ranger"):
 *
 *  - Reload bar (Deadeye): while the cylinder reloads, a thin bar fills over 1.2 s above the cast bar
 *    with the gold sweet spot (the perfect-reload window, from DEADEYE_RELOAD) and "Snap Shut [4]".
 *    Pressing 4 inside the gold = a perfect reload (Hot Rounds); the bar answers with PERFECT /
 *    JAMMED / Reloaded. Ignoring it is fine: the reload just finishes.
 *  - Beast bar (Beastbound): the spirit beast's own bar: its name and health, what it is doing, the
 *    re-forming timer when it has fallen, and its three commands (Hunt / Stand Guard on E, Fanged
 *    Maul on R) as buttons that press those keys.
 *
 * Both draw only for their spec (hidden otherwise) and touch the DOM only on change.
 */
import type { DeepReadonly, SimEvent, Unit, UnitId, World } from '@keystone/sim';
import { BEASTBOUND, BEASTBOUND_AURA, DEADEYE_AURA, DEADEYE_RELOAD } from '@keystone/content';
import type { HudHandlers, HudUpdate } from './hud';
import type { HudWidget, HudWidgetDef } from './widgets';
import { el, setClass, setStyle, setText } from './dom';
import { iconUrl } from './icons';
import { hudNow } from '../game/prediction';
import { playSound } from '../audio/engine';
import './rangerHud.css';

/** How long the result word stays up after a reload. */
const RESULT_MS = 900;

class ReloadBar implements HudWidget {
  private readonly root: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly word: HTMLElement;
  private readonly label: HTMLElement;
  private shown = false;
  private resultUntil = 0;
  private lastPct = -1;

  constructor(host: HTMLElement) {
    this.root = el('div', 'rk-reload hk-hidden', host);
    const track = el('div', 'rk-track', this.root);
    const sweet = el('div', 'rk-sweet', track);
    const lo = DEADEYE_RELOAD.perfectFromMs / DEADEYE_RELOAD.durationMs;
    const hi = DEADEYE_RELOAD.perfectToMs / DEADEYE_RELOAD.durationMs;
    sweet.style.left = `${(lo * 100).toFixed(1)}%`;
    sweet.style.width = `${((hi - lo) * 100).toFixed(1)}%`;
    this.fill = el('div', 'rk-fill', track);
    this.label = el('div', 'rk-label', this.root);
    el('span', '', this.label, 'Snap Shut ');
    el('kbd', '', this.label, '4');
    this.word = el('div', 'rk-word', this.root);
  }

  update(h: HudUpdate, realNow: number): void {
    const me = h.playerId ? h.world.units[h.playerId] : undefined;
    const inst = me?.specId === 'deadeye' ? me.auras.find((a) => a.auraId === DEADEYE_AURA.reloading) : undefined;
    const showing = !!inst || realNow < this.resultUntil;
    if (showing !== this.shown) {
      this.shown = showing;
      setClass(this.root, 'hk-hidden', !showing);
    }
    if (!showing) return;
    setClass(this.root, 'live', !!inst);
    if (!inst) return;
    const el0 = hudNow(h.world) - inst.appliedAt;
    const pct = Math.max(0, Math.min(1, el0 / DEADEYE_RELOAD.durationMs));
    const r = Math.round(pct * 200) / 200;
    if (r !== this.lastPct) {
      this.lastPct = r;
      setStyle(this.fill, 'transform', `scaleX(${r})`);
      setClass(this.root, 'in-window', el0 >= DEADEYE_RELOAD.perfectFromMs && el0 <= DEADEYE_RELOAD.perfectToMs);
    }
  }

  ingest(events: readonly SimEvent[], playerId: UnitId | null, now: number): void {
    if (!playerId) return;
    for (const e of events) {
      if (e.targetID !== playerId) continue;
      if ((e.type === 'applybuff' || e.type === 'refreshbuff') && e.abilityGameID === DEADEYE_AURA.hotRounds) this.result('PERFECT', 'perfect', now, 'spec.perfect');
      else if (e.type === 'applydebuff' && e.abilityGameID === DEADEYE_AURA.jammed) this.result('JAMMED', 'jam', now, 'spec.jam');
      // A reload that ended without a verdict (ignored, or snapped late): plain; a perfect / jam in the same batch overrides it.
      else if (e.type === 'removebuff' && e.abilityGameID === DEADEYE_AURA.reloading) this.result('Reloaded', 'plain', now, null);
      else if (e.type === 'applybuff' && e.abilityGameID === DEADEYE_AURA.reloading) {
        this.root.classList.remove('perfect', 'jam', 'plain');
        setText(this.word, '');
      }
    }
  }

  private result(text: string, cls: string, _now: number, sound: string | null): void {
    this.resultUntil = performance.now() + RESULT_MS;
    this.root.classList.remove('perfect', 'jam', 'plain');
    this.root.classList.add(cls);
    setText(this.word, text);
    this.lastPct = -1;
    setStyle(this.fill, 'transform', 'scaleX(1)');
    if (sound) playSound(sound);
  }
}

/** The beast's command buttons: [label, ability shown, key, active?]. */
interface Cmd {
  root: HTMLButtonElement;
  icon: HTMLImageElement;
  cd: HTMLElement;
  key: 'E' | 'R';
  abilityId: string;
  lastCd: number;
  lastOn: boolean | null;
}

function beastOf(world: DeepReadonly<World>, ownerId: UnitId): DeepReadonly<Unit> | undefined {
  let dead: DeepReadonly<Unit> | undefined;
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (u?.companion?.ownerId !== ownerId) continue;
    if (u.alive) return u;
    dead = u;
  }
  return dead;
}

class BeastBar implements HudWidget {
  private readonly root: HTMLElement;
  private readonly name: HTMLElement;
  private readonly state: HTMLElement;
  private readonly hpFill: HTMLElement;
  private readonly cmds: Cmd[] = [];
  private shown = false;
  private lastHp = -1;
  private lastName = '';
  private lastState = '';

  constructor(host: HTMLElement, handlers: HudHandlers) {
    this.root = el('div', 'rk-beast hk-hidden hk-hit', host);
    const head = el('div', 'rk-beast-head', this.root);
    el('i', 'rk-beast-sigil', head);
    this.name = el('span', 'rk-beast-name', head, 'Spirit Fox');
    this.state = el('span', 'rk-beast-state', head, '');
    const bar = el('div', 'rk-beast-hp', this.root);
    this.hpFill = el('div', 'rk-beast-hp-fill', bar);
    const row = el('div', 'rk-beast-cmds', this.root);
    const mk = (label: string, abilityId: string, key: 'E' | 'R', press: () => void): void => {
      const b = el('button', 'rk-cmd', row);
      b.type = 'button';
      b.title = label;
      const icon = el('img', '', b);
      icon.alt = '';
      icon.src = iconUrl(abilityId, 64) ?? '';
      const cd = el('i', 'rk-cmd-cd', b);
      el('span', 'rk-cmd-label', b, label);
      el('kbd', '', b, key);
      b.addEventListener('click', press);
      this.cmds.push({ root: b, icon, cd, key, abilityId, lastCd: -1, lastOn: null });
    };
    // Hunt and Stand Guard share E (it flips); each button only presses when it would change the stance.
    mk('Hunt', BEASTBOUND.hunt, 'E', () => this.guarding && handlers.bind('E'));
    mk('Stand Guard', BEASTBOUND.guard, 'E', () => !this.guarding && handlers.bind('E'));
    mk('Fanged Maul', BEASTBOUND.maul, 'R', () => handlers.bind('R'));
  }

  private guarding = false;

  update(h: HudUpdate): void {
    const me = h.playerId ? h.world.units[h.playerId] : undefined;
    const on = me?.specId === 'beastbound';
    if (on !== this.shown) {
      this.shown = on;
      setClass(this.root, 'hk-hidden', !on);
      document.body.classList.toggle('rk-has-beast', on);
    }
    if (!on || !me) return;
    const now = hudNow(h.world);
    const beast = beastOf(h.world, me.id);
    this.guarding = me.auras.some((a) => a.auraId === BEASTBOUND_AURA.guarding);
    const name = beast?.name ?? 'Spirit Fox';
    if (name !== this.lastName) setText(this.name, (this.lastName = name));
    const reform = me.auras.find((a) => a.auraId === BEASTBOUND_AURA.reforming);
    const st = !beast?.alive
      ? reform?.expiresAt
        ? `re-forming ${Math.max(0, Math.ceil((reform.expiresAt - now) / 1000))}s`
        : 're-forming'
      : this.guarding
        ? 'guarding you'
        : beast.targetId && h.world.units[beast.targetId]?.faction !== me.faction
          ? 'hunting'
          : 'at heel';
    if (st !== this.lastState) {
      this.lastState = st;
      setText(this.state, st);
      setClass(this.root, 'down', !beast?.alive);
    }
    const hp = beast?.alive ? Math.round((beast.hp / Math.max(1, beast.maxHp)) * 200) / 200 : 0;
    if (hp !== this.lastHp) {
      this.lastHp = hp;
      setStyle(this.hpFill, 'transform', `scaleX(${hp})`);
    }
    for (const c of this.cmds) {
      const active = c.abilityId === BEASTBOUND.maul ? !!beast?.alive : c.abilityId === BEASTBOUND.guard ? this.guarding : !this.guarding;
      if (active !== c.lastOn) {
        c.lastOn = active;
        setClass(c.root, 'on', active);
      }
      if (c.abilityId === BEASTBOUND.maul) {
        const left = h.queries.cooldownRemaining(me.id, BEASTBOUND.maul);
        const frac = left > 0 ? Math.round((left / 10_000) * 50) / 50 : 0;
        if (frac !== c.lastCd) {
          c.lastCd = frac;
          setStyle(c.cd, 'background', frac > 0 ? `conic-gradient(rgba(0,0,0,0.62) ${frac * 360}deg, transparent 0)` : 'transparent');
        }
      }
    }
  }

  dispose(): void {
    document.body.classList.remove('rk-has-beast');
  }
}

/** +ranger-specs: registered in widgets.ts HUD_WIDGETS. */
export const RANGER_HUD_WIDGETS: HudWidgetDef[] = [
  { id: 'reload', region: 'bottomCenter', create: (host) => new ReloadBar(host) },
  { id: 'beast', region: 'bottomCenter', create: (host, ctx) => new BeastBar(host, ctx.handlers) },
];

